import { useCallback, useEffect, useState } from "react";

import * as LocalBrandConfig from "../../../shared/node/BrandConfig";

import { ResolvedThemeName } from "./theme";
import {
  getSystemPreference,
  readVisitorThemePreference,
  setVisitorThemePreference,
} from "./visitorThemePreference";

export type UseActiveBrandThemeOptions = {
  /**
   * The org's configured default theme mode ("auto" | "light" | "dark").
   * Mirrors `Brand.brandThemeMode`. Defaults to "auto" (via
   * `LocalBrandConfig.resolveActiveTheme`'s own default) when omitted.
   */
  brandThemeMode?: "auto" | "light" | "dark";
  /**
   * Whether the visitor is allowed to manually override the resolved
   * theme (mirrors `Brand.allowVisitorThemeOverride`). Defaults to `true`.
   */
  allowVisitorThemeOverride?: boolean;
};

export type UseActiveBrandThemeResult = {
  /** The currently-resolved theme bucket ("light" | "dark"). */
  activeThemeName: ResolvedThemeName;
  /**
   * Sets (and persists to localStorage) the visitor's manual theme
   * preference, immediately updating `activeThemeName` on this and any
   * other mounted instance of this hook (via the `storage`
   * event/re-render) for the current session. A no-op (aside from the
   * persisted value) when `allowVisitorThemeOverride` is `false` — callers
   * building a toggle UI should hide the control entirely in that case
   * rather than relying on this being a no-op.
   */
  setVisitorOverride: (theme: ResolvedThemeName) => void;
};

/**
 * Single source of truth for resolving which theme bucket ("light" |
 * "dark") should currently be active, reactive to:
 *
 * - The org's `brandThemeMode` ("auto" | "light" | "dark") and
 *   `allowVisitorThemeOverride` settings.
 * - The visitor's OS-level `prefers-color-scheme` (live, via a
 *   `matchMedia` change listener) — only relevant when `brandThemeMode`
 *   is "auto" (or unset) and no visitor override is stored.
 * - The visitor's own manually-stored theme preference (persisted to
 *   localStorage under `VISITOR_THEME_STORAGE_KEY` — see
 *   `visitorThemePreference.ts`), which takes precedence over both of the
 *   above when `allowVisitorThemeOverride` is `true`.
 *
 * This wraps `LocalBrandConfig.resolveActiveTheme` (the same resolution
 * logic used server-side/in `getThemes()`) so the precedence rules are
 * defined in exactly one place, rather than being re-implemented
 * separately by `ContentContainer` (which needs the active theme for its
 * `ThemeProvider`/styled-components tree) and `BrandStyleTag` (which needs
 * it for CSS var injection).
 */
export function useActiveBrandTheme(
  options: UseActiveBrandThemeOptions = {},
): UseActiveBrandThemeResult {
  const { brandThemeMode, allowVisitorThemeOverride = true } = options;

  const [systemPreference, setSystemPreference] =
    useState<ResolvedThemeName | null>(() => getSystemPreference());
  const [visitorOverride, setVisitorOverrideState] =
    useState<ResolvedThemeName | null>(() =>
      allowVisitorThemeOverride ? readVisitorThemePreference() : null,
    );

  // React live to OS-level prefers-color-scheme changes.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPreference(event.matches ? "dark" : "light");
    };
    // Safari < 14 only supports addListener/removeListener; both are
    // supported here for broad compatibility.
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Drop any in-memory visitor override if the org later disallows it.
  useEffect(() => {
    if (!allowVisitorThemeOverride) setVisitorOverrideState(null);
  }, [allowVisitorThemeOverride]);

  const setVisitorOverride = useCallback((nextTheme: ResolvedThemeName) => {
    setVisitorThemePreference(nextTheme);
    setVisitorOverrideState(nextTheme);
  }, []);

  const activeThemeName = LocalBrandConfig.resolveActiveTheme({
    allowVisitorThemeOverride,
    brandThemeMode,
    systemPreference,
    visitorOverride,
  });

  return { activeThemeName, setVisitorOverride };
}

export default useActiveBrandTheme;
